import { boxCorners, localToWorld, type Box, type Vec2 } from '@vp/core';
import type { Camera } from '../camera';

export type HandleId = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'rot' | 'p0' | 'p1';

export const HANDLE_SIZE = 8; // CSS px
export const ROT_OFFSET = 24; // CSS px above the top edge

/** Normalized handle positions inside a box (0..1). */
export const HANDLE_POS: Record<Exclude<HandleId, 'rot' | 'p0' | 'p1'>, [number, number]> = {
  nw: [0, 0],
  n: [0.5, 0],
  ne: [1, 0],
  e: [1, 0.5],
  se: [1, 1],
  s: [0.5, 1],
  sw: [0, 1],
  w: [0, 0.5],
};

export interface HandleInfo {
  id: HandleId;
  screen: Vec2;
}

/** Screen positions of handles for a (possibly rotated) world box. */
export function handlesFor(box: Box, camera: Camera, opts: { resize: boolean; rotate: boolean }): HandleInfo[] {
  const out: HandleInfo[] = [];
  const small = box.w * camera.zoom < 24 || box.h * camera.zoom < 24;
  if (opts.resize) {
    for (const id of Object.keys(HANDLE_POS) as Exclude<HandleId, 'rot' | 'p0' | 'p1'>[]) {
      if (small && id.length === 1) continue; // hide edge handles on tiny boxes
      const [u, v] = HANDLE_POS[id];
      const w = localToWorld(box, { x: u * box.w, y: v * box.h });
      out.push({ id, screen: camera.worldToScreen(w.x, w.y) });
    }
  }
  if (opts.rotate) {
    const top = localToWorld(box, { x: box.w / 2, y: 0 });
    const s = camera.worldToScreen(top.x, top.y);
    // offset perpendicular to the top edge in screen space
    const c = Math.cos(box.rotation);
    const sn = Math.sin(box.rotation);
    out.push({ id: 'rot', screen: { x: s.x + sn * ROT_OFFSET, y: s.y - c * ROT_OFFSET } });
  }
  return out;
}

export function hitHandle(handles: HandleInfo[], sx: number, sy: number, tol = HANDLE_SIZE): HandleId | null {
  for (let i = handles.length - 1; i >= 0; i--) {
    const h = handles[i];
    if (Math.abs(h.screen.x - sx) <= tol && Math.abs(h.screen.y - sy) <= tol) return h.id;
  }
  return null;
}

export function boxScreenCorners(box: Box, camera: Camera): Vec2[] {
  return boxCorners(box).map((p) => camera.worldToScreen(p.x, p.y));
}

export function cursorFor(h: HandleId, rotation: number): string {
  if (h === 'rot') return 'grab';
  if (h === 'p0' || h === 'p1') return 'crosshair';
  const base: Record<string, number> = { e: 0, se: 45, s: 90, sw: 135, w: 180, nw: 225, n: 270, ne: 315 };
  let deg = (base[h] + (rotation * 180) / Math.PI) % 180;
  if (deg < 0) deg += 180;
  const idx = Math.round(deg / 45) % 4;
  return ['ew-resize', 'nwse-resize', 'ns-resize', 'nesw-resize'][idx];
}
